import * as React from 'react'
import { CloseOutline } from '@easy-eva-icons/react'
import { Icon } from 'src/design-system'
import styles from './SkipWarning.module.scss'
import { PositionSkipped, User, UserSkipped } from './types'
import { useWfiContext } from './WfiContext'

/**
 * `positions`, `users`, and `inferredSalaries` are all mutually exclusive with each other.
 */
type SkipWarningProps =
  | { positions: PositionSkipped[]; onClose: () => void }
  | { users: UserSkipped[]; onClose: () => void }
  | { inferredSalaries: User[]; onClose: () => void }

export const SkipWarning: React.VFC<SkipWarningProps> = (props) => {
  const { orgSnapshot } = useWfiContext()
  if (orgSnapshot == null) return null

  const hasPositions = 'positions' in props && props.positions.length > 0
  const hasSalaries =
    'inferredSalaries' in props && props.inferredSalaries.length > 0
  const hasUsers = 'users' in props && props.users.length > 0

  const title = hasPositions
    ? 'Skipped roles'
    : hasSalaries
    ? 'Some salaries were implied'
    : 'Skipped users'

  return (
    <div>
      <div className={styles.overlay} onClick={props.onClose} />
      <aside className={styles.sidebar}>
        <div className={styles.header}>
          <h3>{title}</h3>

          <button onClick={props.onClose}>
            <Icon size="sm" style={{ color: 'currentcolor' }}>
              <CloseOutline />
            </Icon>
          </button>
        </div>

        {hasPositions && (
          <>
            <p className={styles.description}>
              The roles listed below were skipped because no users are currently
              assigned to them.
            </p>
            <ul>
              {props.positions.map((position, index) => (
                <li key={index}>
                  {position.name}{' '}
                  <span className={styles.teamName}>
                    (
                    {orgSnapshot.teamsById.get(position.team_id)?.name ??
                      'Invalid team id'}
                    )
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        {hasUsers && (
          <>
            <p className={styles.description}>
              The users listed below are not included in the organization size
              because they are not assigned to a position.
            </p>
            <ul>
              {props.users.map((user, index) => (
                <li key={index}>{user.name}</li>
              ))}
            </ul>
          </>
        )}

        {hasSalaries && (
          <>
            <p className={styles.description}>
              If one or multiple salary ranges match employee profiles we select
              the median of the range/s. View and edit your employee profiles to
              add a specific salary and improve accuracy.
            </p>
            <ul>
              {props.inferredSalaries.map((user, index) => (
                <li className={styles.inferredSalaryItem} key={index}>
                  <p>
                    {orgSnapshot.positionsById.get(user.position_id)?.name ??
                      'Invalid position id'}
                  </p>
                  <p className={styles.inferredSalaryUser}>{user.name}</p>
                  <p className={styles.inferredSalaryTeam}>
                    {orgSnapshot.teamsById.get(user.team_id)?.name ??
                      'Invalid team id'}
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  )
}
